export const FLYER_INPUT_MAX_BYTES = 15 * 1024 * 1024;
export const FLYER_MAX_LONG_EDGE = 1800;
export const FLYER_WEBP_QUALITY = 0.86;
export const FLYER_ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export interface OptimizedFlyer {
  file: File;
  width: number;
  height: number;
  originalSize: number;
}

export function validateFlyerInput(file: File) {
  if (!FLYER_ALLOWED_TYPES.has(file.type)) {
    throw new Error("Use a JPG, PNG, or WebP flyer image.");
  }
  if (file.size <= 0) {
    throw new Error("The selected flyer is empty.");
  }
  if (file.size > FLYER_INPUT_MAX_BYTES) {
    throw new Error("Flyer images must be 15 MB or smaller.");
  }
}

function webpName(name: string) {
  const base = name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || "flyer";
  return `${base}.webp`;
}

function resizeImage(bitmap: ImageBitmap, maxLongEdge: number, type: "image/webp", quality: number) {
  const longest = Math.max(bitmap.width, bitmap.height);
  const scale = Math.min(1, maxLongEdge / longest);
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Unable to prepare image canvas.");
  }

  context.drawImage(bitmap, 0, 0, width, height);

  return new Promise<{ blob: Blob; width: number; height: number }>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Unable to optimize flyer image."));
          return;
        }
        resolve({ blob, width, height });
      },
      type,
      quality
    );
  });
}

export async function optimizeFlyerImage(file: File): Promise<OptimizedFlyer> {
  validateFlyerInput(file);
  const bitmap = await createImageBitmap(file);

  try {
    if (bitmap.width > 9000 || bitmap.height > 9000) {
      throw new Error("Flyer dimensions are too large for browser optimization.");
    }

    const optimized = await resizeImage(bitmap, FLYER_MAX_LONG_EDGE, "image/webp", FLYER_WEBP_QUALITY);
    return {
      file: new File([optimized.blob], webpName(file.name), { type: "image/webp" }),
      width: optimized.width,
      height: optimized.height,
      originalSize: file.size
    };
  } finally {
    bitmap.close();
  }
}
